// Les erreurs d'une liste de périodes non travaillées pendant la saisie, avant tout envoi : celles du concern
// `NonWorkingCalendar` (dates, années 2000-2100, 366 jours, libellé), avec ses textes, et le chevauchement N8
// porté par la ligne qui vient APRÈS dans l'ordre reçu. Partagé par le tiroir « Réglages » du planning
// (`plan_settings_controller.js`) et l'onglet agence (`agency_calendar_controller.js`, A.15, 30/10) : la même
// règle aux deux écrans, le serveur garde la sienne.

import {
  dateRangeText,
  interpolate,
  MAX_LABEL_LENGTH,
  periodDateErrorKey,
} from './planning_calendar_date_formats.js';

/**
 * Le message de chacune des périodes, dans l'ordre reçu ('' sans erreur). Une période chevauche la PREMIÈRE
 * (par date de début) des périodes valides qui la précèdent dans cet ordre.
 *
 * @param {Array<{label: string, start: string, end: string, range?: string, draft?: boolean}>} periods
 *   `range` : le texte de dates déjà formaté par le serveur, s'il est connu ; `draft` : un brouillon dont une
 *   date manque n'a pas d'erreur (son bouton dit déjà « Choisissez les dates »).
 * @param {Object} messages `overlap` (brut, `%{label}` et `%{dates}`), `date_range` `{ forms, month_names }`,
 *   `end_before_start`, `out_of_years`, `too_long`, `label_too_long`, `default_label`.
 * @returns {string[]}
 */
export function periodErrors(periods, messages) {
  const items = periods.map((period) => ({
    ...period,
    label: String(period.label ?? ''),
    error: periodDateErrorKey(period.start, period.end),
  }));
  return items.map((item, index) => {
    const errors = [];
    if (item.error && !(item.draft && item.error === 'invalid_dates')) {
      errors.push(messages[item.error] ?? '');
    }
    if (messages.label_too_long && item.label.trim().length > MAX_LABEL_LENGTH) {
      errors.push(messages.label_too_long);
    }
    if (!item.error) {
      const other = items
        .slice(0, index)
        .filter((prior) => !prior.error && prior.start <= item.end && item.start <= prior.end)
        .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0))[0];
      if (other) errors.push(overlapMessage(other, messages));
    }
    return errors.filter(Boolean).join(' ');
  });
}

function overlapMessage(other, messages) {
  const label = other.label.trim() ? other.label.trim() : (messages.default_label ?? '');
  // `date_range` : `{ forms: { same_day, … }, month_names }` (`PlanSettingsView#messages`).
  const range = messages.date_range ?? {};
  const forms = range.forms ?? range;
  const monthNames = range.month_names ?? messages.month_names ?? [];
  const dates = other.range || dateRangeText(other.start, other.end, forms, monthNames);
  return interpolate(messages.overlap, { label, dates });
}
